import { useAppSelector } from "../../../../../store/hooks";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import Dropdown from "../../../Dropdown/Dropdown";
import { updateStructureComponent } from "../../../../../store/reducers/builderReducers/structure/structureActions";
import { useEffect, useState, useRef } from "react";
import { SectionComponent } from "../../../../_default/interfaces/base";
import React from "react";
import { fetchApiGet, fetchApiGetUuid } from "../../../../../api/api";
import { EventComponent } from "../../../../_default/interfaces/base";
import "./EventsMenu.scss";
import Select from "react-select";
import {
  TextEditorSelectStyle,
  DefaultReactSelectStyle,
} from "../../../ReactSelectStyle/ReactSelectStyle";
import { Pages } from "../../../../../store/reducers/builderReducers/pages/pagesApi";
import {
  languageCodeFromActiveLanguage,
  languageCodesEqual,
} from "../../../../../utils/languageCode";

interface Props {
  id: number;
  triggerResize: () => void;
}

interface EventGroup {
  hash: string;
  groupName: string;
  hasTickets: boolean;
  location: string;
}

interface Event {
  hash: string;
  name: string;
  startDate?: string;
  endDate?: string;
  location?: string;
}

type EventType = "single" | "recurring";

export default function EventsMenu({ id, triggerResize }: Props) {
  const component = useAppSelector(
    (state) => state.builder.structure.components[id]
  )! as EventComponent;

  const webpages = useAppSelector(
    (state) => state.builder.pages.list as Pages["list"]
  );
  const languages = useAppSelector((state) => state.builder.languageSettings);

  // Event type selection state
  const [eventType, setEventType] = useState<EventType>("recurring");

  // Add to cart button settings
  const [showAddToCartButton, setShowAddToCartButton] = useState<boolean>(
    component.attributes.show_add_to_cart_button !== false
  );
  const [addToCartButtonText, setAddToCartButtonText] = useState<string>(
    component.attributes.add_to_cart_button_text || ""
  );
  const [redirectPageId, setRedirectPageId] = useState<number | null>(
    component.attributes.add_to_cart_redirect_page_id || null
  );
  const [redirectUrl, setRedirectUrl] = useState<string>(
    component.attributes.add_to_cart_redirect_url || ""
  );

  // Event Groups state
  const [loadingGroups, setLoadingGroups] = useState<boolean>(false);
  const [errorGroups, setErrorGroups] = useState<string | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<EventGroup | null>(null);

  // Events state
  const [filteredEvents, setFilteredEvents] = useState<Event[]>([]);
  const [filteredEventGroups, setFilteredEventGroups] = useState<EventGroup[]>(
    []
  );
  const [loadingEvents, setLoadingEvents] = useState<boolean>(false);
  const [errorEvents, setErrorEvents] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [isMenuClicked, setIsMenuClicked] = useState<boolean>(false);

  // Timeout for debouncing search
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Initialize component with saved event if available
  useEffect(() => {
    const { event_hash, event_group_hash, event_name, event_group_name } = component.attributes;

    if (typeof event_group_hash === "string" && event_group_hash) {
      setEventType("recurring");
      fetchEventGroupByHash(event_group_hash);
    } else if (typeof event_hash === "string" && event_hash) {
      setEventType("single");
      fetchEventByRef(event_hash);
    } else if (event_name) {
      setSearchQuery(event_name);
    } else if (event_group_name) {
      setEventType("recurring");
      setSearchQuery(event_group_name);
    }

    triggerResize();
  }, []);

  // Watch for component attribute changes from outside
  useEffect(() => {
    if (
      eventType === "single" &&
      component.attributes.event_hash &&
      (!selectedEvent || selectedEvent.hash !== component.attributes.event_hash)
    ) {
      fetchEventByRef(component.attributes.event_hash);
    } else if (
      eventType === "recurring" &&
      component.attributes.event_group_hash &&
      (!selectedGroup || selectedGroup.hash !== component.attributes.event_group_hash)
    ) {
      fetchEventGroupByHash(component.attributes.event_group_hash);
    }
  }, [
    component.attributes.event_hash,
    component.attributes.event_group_hash,
    eventType,
    selectedEvent,
    selectedGroup,
  ]);

  const fetchEventByRef = async (eventRef: string) => {
    try {
      setLoadingEvents(true);
      setErrorEvents(null);

      const response = await fetchApiGet<Event>(`/event/${eventRef}`);

      if (response) {
        setSelectedEvent(response);
        setSearchQuery(response.name);
      } else {
        console.error("Event not found for ref:", eventRef);
        setErrorEvents("Event not found");
      }

      setLoadingEvents(false);
    } catch (err) {
      console.error("Error fetching event:", err);
      setErrorEvents("Error fetching event");
      setLoadingEvents(false);
    }
  };

  const fetchEventGroupByHash = async (groupHash: string) => {
    try {
      setLoadingGroups(true);
      setErrorGroups(null);

      const response = await fetchApiGetUuid<EventGroup>(
        `/events-groups/${groupHash}`
      );

      if (response) {
        setSelectedGroup(response);
        setSearchQuery(response.groupName);
      } else {
        console.error("Event group not found for hash:", groupHash);
        setErrorGroups("Event group not found");
      }

      setLoadingGroups(false);
    } catch (err) {
      console.error("Error fetching event group by hash:", err);
      setErrorGroups("Error fetching event group");
      setLoadingGroups(false);
    }
  };

  const searchEvents = async (query: string) => {
    try {
      setLoadingEvents(true);
      setErrorEvents(null);

      // We don't know the exact format, so use any and check the structure
      const response = await fetchApiGet<any>(
        `/events?name=${encodeURIComponent(query)}`
      );

      // Handle different response formats
      if (Array.isArray(response)) {
        // Response is directly an array of events
        setFilteredEvents(response as Event[]);
      } else if (
        response &&
        typeof response === "object" &&
        "data" in response &&
        Array.isArray(response.data)
      ) {
        // Response has a data property that is an array
        setFilteredEvents(response.data as Event[]);
      } else {
        // Unexpected response format
        console.error("Unexpected response format:", response);
        setFilteredEvents([]);
        setErrorEvents("Invalid response format");
      }

      setLoadingEvents(false);
    } catch (err) {
      console.error("Error fetching events:", err);
      setErrorEvents("Failed to load events");
      setLoadingEvents(false);
    }
  };

  const searchEventGroups = async (query: string) => {
    try {
      setLoadingGroups(true);
      setErrorGroups(null);

      // Search for event groups with the query
      const response = await fetchApiGetUuid<any>(
        `/events-groups?name=${encodeURIComponent(query)}`
      );

      // Handle different response formats
      if (Array.isArray(response)) {
        // Response is directly an array of event groups
        setFilteredEventGroups(response as EventGroup[]);
      } else if (
        response &&
        typeof response === "object" &&
        "data" in response &&
        Array.isArray(response.data)
      ) {
        // Response has a data property that is an array
        setFilteredEventGroups(response.data as EventGroup[]);
      } else {
        // Unexpected response format
        console.error("Unexpected event group response format:", response);
        setFilteredEventGroups([]);
        setErrorGroups("Invalid response format");
      }

      setLoadingGroups(false);
    } catch (err) {
      console.error("Error fetching event groups:", err);
      setErrorGroups("Failed to load event groups");
      setLoadingGroups(false);
    }
  };

  const handleEventTypeChange = (type: EventType) => {
    setEventType(type);
    setSearchQuery("");
    setSelectedEvent(null);
    setSelectedGroup(null);
    setFilteredEvents([]);
    setFilteredEventGroups([]);
    setIsSearching(false);

    // Clear the attributes in the component
    if (type === "single") {
      updateStructureComponent(id, "attributes", {
        event_group_hash: null,
        event_group_name: null,
      });
    } else {
      updateStructureComponent(id, "attributes", {
        event_hash: null,
        event_name: null,
      });
    }
  };

  const handleSearchFocus = () => {
    setIsSearching(true);
    // Always perform search on focus, even with empty query, to show initial results
    if (eventType === "single" && !selectedEvent) {
      searchEvents(searchQuery);
    } else if (eventType === "recurring" && !selectedGroup) {
      searchEventGroups(searchQuery);
    }
    triggerResize();
  };

  const handleInputWrapperClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMenuClicked(true);
    if (searchRef.current) {
      searchRef.current.focus();
    }
  };

  const handleSearchBlur = (e: React.FocusEvent) => {
    // Only close the dropdown if we're not clicking inside the menu
    if (!isMenuClicked) {
      setTimeout(() => {
        setIsSearching(false);
        triggerResize();
      }, 200);
    }
    setIsMenuClicked(false);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);

    // Clear existing timeout
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    // Set new timeout for debounce (300ms)
    searchTimeout.current = setTimeout(() => {
      if (eventType === "single") {
        searchEvents(value);
      } else {
        searchEventGroups(value);
      }
    }, 300);
  };

  const handleSelectEvent = (event: Event) => {
    setSelectedEvent(event);
    setSearchQuery(event.name);
    setIsSearching(false);

    updateStructureComponent(id, "attributes", {
      event_hash: event.hash ?? null,
      event_name: event.name,
    });

    triggerResize();
  };

  const handleSelectEventGroup = (group: EventGroup) => {
    setSelectedGroup(group);
    setSearchQuery(group.groupName);
    setIsSearching(false);

    updateStructureComponent(id, "attributes", {
      event_group_hash: group.hash,
      event_group_name: group.groupName,
    });

    triggerResize();
  };

  const handleClearSelection = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent input focus

    if (eventType === "single") {
      setSelectedEvent(null);
      updateStructureComponent(id, "attributes", {
        event_hash: null,
        event_name: null,
      });
    } else {
      setSelectedGroup(null);
      updateStructureComponent(id, "attributes", {
        event_group_hash: null,
        event_group_name: null,
      });
    }

    setSearchQuery("");
  };

  return (
    <div className="d-flex flex-column">
      {/* Event Type Selection */}
      <div
        style={{
          gap: "10px",
        }}
        className="d-flex align-items-center"
      >
        <div
          className="d-flex align-items-center mr-3 cursor-pointer"
          onClick={() => {
            // Reeturn
            return;
          }}
          style={{ cursor: "pointer", color: "#888" }}
        >
          {eventType === "single" ? (
            <RadioButtonCheckedIcon
              style={{ fontSize: "16px", marginRight: "4px" }}
            />
          ) : (
            <RadioButtonUncheckedIcon
              style={{ fontSize: "16px", marginRight: "4px" }}
            />
          )}
          <span>Single event</span>
        </div>

        <div
          className="d-flex align-items-center cursor-pointer"
          onClick={() => handleEventTypeChange("recurring")}
          style={{ cursor: "pointer" }}
        >
          {eventType === "recurring" ? (
            <RadioButtonCheckedIcon
              style={{ fontSize: "16px", marginRight: "4px" }}
            />
          ) : (
            <RadioButtonUncheckedIcon
              style={{ fontSize: "16px", marginRight: "4px" }}
            />
          )}
          <span>Recurring event</span>
        </div>
      </div>

      {/* Event Search */}
      <div
        className="d-flex flex-wrap align-items-center"
        style={{ marginTop: "3px" }}
      >
        <span>{eventType === "single" ? "Event:" : "Event group:"}</span>
        <div
          className="b-mx-1 content-width-menu_input position-relative"
          style={{ width: "200px" }}
        >
          {/* Search Input with Selected Event/Group Indicator */}
          <div
            className="d-flex align-items-center"
            style={{
              borderRadius: "4px",
              padding: "2px 4px",
              width: "100%",
              cursor: "pointer",
            }}
            onClick={handleInputWrapperClick}
          >
            <input
              id="event-search-input"
              ref={searchRef}
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={handleSearchFocus}
              onBlur={handleSearchBlur}
              placeholder={
                (eventType === "single" && selectedEvent) ||
                (eventType === "recurring" && selectedGroup)
                  ? ""
                  : eventType === "single"
                  ? "Search for events..."
                  : "Search for event groups..."
              }
              style={{
                background: "transparent",
                border: "none",
                color: "#fff",
                width: "100%",
                outline: "none",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            />
            {(eventType === "single" && selectedEvent) ||
            (eventType === "recurring" && selectedGroup) ? (
              <ClearIcon
                style={{
                  fontSize: "16px",
                  marginLeft: "4px",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
                onClick={handleClearSelection}
              />
            ) : (
              <SearchIcon
                style={{
                  fontSize: "16px",
                  marginLeft: "4px",
                  flexShrink: 0,
                }}
              />
            )}
          </div>

          {/* Search Results */}
          {isSearching && (
            <div
              style={{
                position: "absolute",
                width: "100%",
                maxHeight: "200px",
                overflowY: "auto",
                top: "24px", // Adjusted to account for input height
                background: "#323232",
                borderRadius: "2px",
                zIndex: 1000,
              }}
              onMouseDown={(e) => {
                // Prevent blur when clicking inside the dropdown
                e.preventDefault();
                setIsMenuClicked(true);
              }}
            >
              {eventType === "single" ? (
                // Single Events Results
                loadingEvents ? (
                  <div className="dropdown-item builder-dropdown__item">
                    Loading events...
                  </div>
                ) : errorEvents ? (
                  <div className="dropdown-item builder-dropdown__item">
                    {errorEvents}
                  </div>
                ) : filteredEvents.length > 0 ? (
                  filteredEvents.map((event) => (
                    <div
                      key={event.hash}
                      className={
                        "dropdown-item builder-dropdown__item" +
                        (selectedEvent?.hash === event.hash ||
                        false
                          ? " builder-dropdown__item--active"
                          : "")
                      }
                      onMouseDown={(e) => {
                        e.preventDefault(); // Prevent blur before click
                        handleSelectEvent(event);
                      }}
                    >
                      {event.name}
                    </div>
                  ))
                ) : searchQuery ? (
                  <div className="dropdown-item builder-dropdown__item">
                    No events found
                  </div>
                ) : null
              ) : // Event Groups Results
              loadingGroups ? (
                <div className="dropdown-item builder-dropdown__item">
                  Loading event groups...
                </div>
              ) : errorGroups ? (
                <div className="dropdown-item builder-dropdown__item">
                  {errorGroups}
                </div>
              ) : filteredEventGroups.length > 0 ? (
                filteredEventGroups.map((group) => (
                  <div
                    key={group.hash}
                    className={
                      "dropdown-item builder-dropdown__item" +
                      (selectedGroup?.hash === group.hash
                        ? " builder-dropdown__item--active"
                        : "")
                    }
                    onMouseDown={(e) => {
                      e.preventDefault(); // Prevent blur before click
                      handleSelectEventGroup(group);
                    }}
                  >
                    {group.groupName}
                  </div>
                ))
              ) : (
                <div className="dropdown-item builder-dropdown__item">
                  No event groups found
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Divider */}
      <div
        style={{
          borderTop: "1px solid #444",
          margin: "10px 0",
        }}
      />

      {/* Add to Cart Button Settings */}
      <div className="d-flex flex-column" style={{ gap: "5px" }}>
        {/* Show/Hide Button Toggle */}
        <div className="d-flex align-items-center" style={{ gap: "8px" }}>
          <span>Show "Add to cart" button:</span>
          <label className="switch">
            <input
              type="checkbox"
              checked={showAddToCartButton}
              onChange={(e) => {
                setShowAddToCartButton(e.target.checked);
                updateStructureComponent(
                  id,
                  "attributes.show_add_to_cart_button",
                  e.target.checked
                );
              }}
            />
            <span className="slider round"></span>
          </label>
        </div>

        {/* Button Text (only show if button is visible) */}
        {showAddToCartButton && (
          <>
            <div
              className="d-flex flex-wrap align-items-center"
              style={{ gap: "8px" }}
            >
              <span>Button text:</span>
              <input
                type="text"
                value={addToCartButtonText}
                onChange={(e) => {
                  setAddToCartButtonText(e.target.value);
                  updateStructureComponent(
                    id,
                    "attributes.add_to_cart_button_text",
                    e.target.value
                  );
                }}
                className=""
                placeholder="Lisa ostukorvi"
                style={{
                  background: "#444",
                  border: "0px solid #555",
                  borderRadius: "3px",
                  color: "#fff",
                  height: "22px",
                  paddingLeft: "5px",
                  paddingRight: "5px",
                  width: "200px",
                  boxShadow:
                    "0px 0.5px 1px rgba(0, 0, 0, 0.8), inset 0px 0.5px 0.5px rgba(255, 255, 255, 0.12)",
                  outline: "none",
                }}
              />
            </div>

            {/* Redirect Page Selection */}
            <div
              className="d-flex flex-wrap align-items-center"
              style={{ gap: "8px" }}
            >
              <span>Redirect Page:</span>
              <div style={{ width: "200px" }}>
                <Select
                  onChange={(e: any) => {
                    if (e) {
                      setRedirectPageId(e.pageId);
                      setRedirectUrl(e.value);
                      updateStructureComponent(
                        id,
                        "attributes.add_to_cart_redirect_page_id",
                        e.pageId
                      );
                      updateStructureComponent(
                        id,
                        "attributes.add_to_cart_redirect_url",
                        e.value
                      );
                    } else {
                      setRedirectPageId(null);
                      setRedirectUrl("");
                      updateStructureComponent(
                        id,
                        "attributes.add_to_cart_redirect_page_id",
                        null
                      );
                      updateStructureComponent(
                        id,
                        "attributes.add_to_cart_redirect_url",
                        ""
                      );
                    }
                  }}
                  isClearable={true}
                  isSearchable={true}
                  className="m-0"
                  styles={{
                    ...TextEditorSelectStyle,
                    container: (base) => ({
                      ...base,
                      width: "100%",
                    }),
                    option: (styles, data) => ({
                      ...DefaultReactSelectStyle.option!(styles, data),
                      textAlign: "left",
                      padding: "4px 12px",
                    }),
                    control: (base, state) => ({
                      ...base,
                      background: "#444",
                      border: "0px solid #555",
                      borderRadius: "3px",
                      color: "#fff",
                      minHeight: "22px",
                      height: "22px",
                      paddingLeft: "5px",
                      paddingRight: "5px",
                      boxShadow:
                        "0px 0.5px 1px rgba(0, 0, 0, 0.8), inset 0px 0.5px 0.5px rgba(255, 255, 255, 0.12)",
                      width: "100%",
                      "&:hover": {
                        borderColor: "#9b9b9b",
                      },
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      padding: "0px",
                      minHeight: "20px",
                    }),
                    input: (base) => ({
                      ...base,
                      color: "#fff",
                      margin: "0px",
                      padding: "0px",
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: "#fff",
                      margin: "0px",
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: "#ccc",
                      margin: "0px",
                    }),
                  }}
                  noOptionsMessage={() => "No pages found"}
                  value={
                    redirectPageId
                      ? (() => {
                          const groupedOptions: any[] = [];
                          languages.activeLanguages.forEach((language) => {
                            const filteredWebpages = webpages.filter((page) =>
                              languageCodesEqual(
                                page.languageCode,
                                languageCodeFromActiveLanguage(language)
                              )
                            );
                            filteredWebpages.forEach((page) => {
                              if (page.id === redirectPageId) {
                                groupedOptions.push({
                                  label: page.name,
                                  value: page.url,
                                  pageId: page.id,
                                });
                              }
                            });
                          });
                          return groupedOptions[0] || null;
                        })()
                      : null
                  }
                  options={(() => {
                    const groupedOptions: any[] = [];
                    languages.activeLanguages.forEach((language) => {
                      const filteredWebpages = webpages.filter((page) =>
                        languageCodesEqual(
                          page.languageCode,
                          languageCodeFromActiveLanguage(language)
                        )
                      );
                      groupedOptions.push({
                        label: languageCodeFromActiveLanguage(language),
                        options: filteredWebpages
                          .map((page) => ({
                            label: page.name,
                            value: page.url,
                            pageId: page.id,
                          }))
                          .sort((a, b) => a.label.localeCompare(b.label)),
                      });
                    });
                    return groupedOptions;
                  })()}
                  placeholder="None (no redirect)"
                  formatGroupLabel={(data: any) => (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "0px",
                        margin: "0px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "10px",
                          color: "#e7e7e7",
                          fontWeight: "bold",
                        }}
                      >
                        {data.label}
                      </span>
                      <span
                        style={{
                          fontSize: "10px",
                          color: "#e7e7e7",
                          fontWeight: "bold",
                          backgroundColor: "#2e2e2e",
                          padding: "3px 5px",
                          borderRadius: "2px",
                        }}
                      >
                        {data.options.length}
                      </span>
                    </div>
                  )}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
